import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { AppLayout } from "@/components/AppLayout";
import { SessionFeed } from "@/components/SessionFeed";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Receipt, Image as ImageIcon } from "lucide-react";
import { WeeklySummary } from "@/components/WeeklySummary";

export default function ParentDashboard() {
  const [sessions, setSessions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [children, setChildren] = useState([]);
  const [payingId, setPayingId] = useState(null);

  const load = useCallback(async () => {
    const [sess, inv, kids] = await Promise.all([
      api.get("/sessions"),
      api.get("/invoices"),
      api.get("/students"),
    ]);
    setSessions(sess.data);
    setInvoices(inv.data);
    setChildren(kids.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmt = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

  const pay = async (invoiceId) => {
    setPayingId(invoiceId);
    try {
      const { data } = await api.post("/payments/checkout", {
        invoice_id: invoiceId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPayingId(null);
    }
  };

  const outstanding = invoices.filter((i) => i.status !== "paid");

  return (
    <AppLayout
      title={children.length === 1 ? `${children[0].name}'s Progress` : "Your Children's Progress"}
      subtitle="See what was completed in every session and manage your invoices."
    >
      <Tabs defaultValue="feed">
        <TabsList className="mb-6">
          <TabsTrigger value="feed" data-testid="tab-feed"><ImageIcon className="h-4 w-4 mr-1.5" />Session Feed</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <Receipt className="h-4 w-4 mr-1.5" />Invoices
            {outstanding.length > 0 && (
              <span className="ml-2 rounded-full bg-foreground text-background text-[10px] px-1.5 py-0.5">{outstanding.length}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="feed">
          {children.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {children.map((c) => (
                <WeeklySummary key={c.id} studentId={c.id} studentName={c.name} label={`${c.name}'s Weekly Summary`} />
              ))}
            </div>
          )}
          <SessionFeed sessions={sessions} emptyText="No sessions have been shared yet. Check back soon." />
        </TabsContent>

        <TabsContent value="invoices">
          <div className="space-y-4" data-testid="parent-invoice-list">
            {invoices.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground text-sm">
                You have no invoices.
              </div>
            ) : invoices.map((inv) => (
              <div key={inv.id} className="rounded-lg border border-border bg-card p-5 sm:p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono-tt text-xs text-muted-foreground">{inv.invoice_number}</span>
                      <StatusBadge status={inv.status} />
                    </div>
                    {inv.student_name && <div className="text-sm mt-1 text-muted-foreground">For {inv.student_name}</div>}
                    <div className="font-brand text-3xl font-light mt-3">{fmt(inv.total)}</div>
                    {inv.due_date && <div className="text-xs text-muted-foreground mt-1">Due {inv.due_date}</div>}
                  </div>
                  {inv.status !== "paid" && (
                    <Button onClick={() => pay(inv.id)} disabled={payingId === inv.id} className="rounded-full" data-testid="stripe-pay-now-button">
                      {payingId === inv.id ? "Redirecting…" : "Pay Now"}
                    </Button>
                  )}
                </div>
                <div className="mt-4 border-t border-border pt-3 space-y-1">
                  {inv.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{it.description}</span>
                      <span>{fmt(it.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
